import { Input, Label } from '@kmgestao/ui';

import type { FormField } from '../../schemas.js';

export interface TextFieldProps {
  field: FormField;
  value: string;
  onChange: (next: string) => void;
  onBlur?: (() => void) | undefined;
  error?: string | undefined;
  disabled?: boolean | undefined;
  idPrefix?: string | undefined;
}

/**
 * Campo de texto de linha única. Usado para `type: 'text'`.
 *
 * **Acessibilidade**:
 *   - `<Label>` associado via `htmlFor` + `id`.
 *   - `aria-invalid` e `aria-describedby` quando há erro.
 *   - Mensagem de erro em `role="alert"` para screen readers.
 */
export function TextField({
  field,
  value,
  onChange,
  onBlur,
  error,
  disabled,
  idPrefix = 'form',
}: TextFieldProps) {
  const inputId = `${idPrefix}-${field.id}`;
  const descId = field.description ? `${inputId}-desc` : undefined;
  const errorId = error ? `${inputId}-err` : undefined;

  return (
    <div className="space-y-2">
      <Label htmlFor={inputId}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive"> *</span> : null}
      </Label>
      {field.description ? (
        <p id={descId} className="text-xs text-muted-foreground">
          {field.description}
        </p>
      ) : null}
      <Input
        id={inputId}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={field.placeholder}
        autoComplete={field.autocomplete}
        inputMode={field.inputmode}
        maxLength={field.maxlength}
        hasError={!!error}
        disabled={disabled}
        aria-describedby={[descId, errorId].filter(Boolean).join(' ') || undefined}
        aria-invalid={!!error}
      />
      {error ? (
        <p id={errorId} className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
